import * as React from 'react';
import { useSelector } from 'react-redux';
import { getIsGooglePlayStore, getIsAppleAppStore, getIsAmazonAppStore, getIsHuaweiAppGallery } from '../helpers';
import { RootState } from '../reducers/reducers';
import { AppComponentProps } from '../typescriptHelpers';
import { runwayColors, tagStyle } from './styles/globalStyles';
import styles from './styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import EditAppleAppReleaseDefaults from './EditAppleAppReleaseDefaults';
import Tag from 'antd/lib/tag';
import Space from 'antd/lib/space';
import EditGoogleAppReleaseDefaults from './EditGoogleAppReleaseDefaults';
import { displayStoreNameForAppStoreType } from '../helpers/displayHelpers';
import GooglePlayStoreReleaseSettingsDescriptions from './appSettings/GooglePlayStoreReleaseSettingsDescriptions';
import AppleAppStoreReleaseDefaultsDescriptions from './appSettings/AppleAppStoreReleaseDefaultsDescriptions';
import { selectAppSettingAutomations, selectAppStoreDefaults, selectAppStoreType } from '../selectors/selectors';
import AmazonAppStoreReleaseSettingsDescriptions from './appSettings/EditAmazonAppReleaseDefaults';
import Button from 'antd/lib/button';
import {
    AutomationAutoStagedRollout,
    AutomationType,
    GoogleAppStoreSettings,
    HuaweiAppGallerySettings,
} from '../types';
import HuaweiAppGalleryReleaseSettingsDescriptions from '@/components/appSettings/HuaweiAppGalleryReleaseSettingsDescriptions';
import EditHuaweiAppReleaseDefaults from '@/components/EditHuaweiAppReleaseDefaults';

const AppReleaseDefaults: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const [releaseDefaultsModalVisible, setReleaseDefaultsModalVisible] = React.useState(false);

    const appReleaseDefaults = useSelector((state: RootState) => selectAppStoreDefaults(state, props.appId));

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const isAppleStore = getIsAppleAppStore(appStoreType);
    const isGoogleStore = getIsGooglePlayStore(appStoreType);
    const isAmazonAppStore = getIsAmazonAppStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    const needsSetup = !appReleaseDefaults;

    // TODO: #WORKFLOWS# in the future there might be multiple automations of the same type
    const autoStagedRolloutAutomation = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        return automations.find((automation) => automation.type === AutomationType.AutoStagedRollout);
    }) as AutomationAutoStagedRollout;

    const autoStagedRolloutAutomationId = autoStagedRolloutAutomation?.id;

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space size={18} align="center">
                    <Text style={styles.appSettingsSectionTitleStyle}>Release defaults</Text>
                    {needsSetup && (
                        <Tag color={runwayColors.statusYellow} style={tagStyle}>
                            Needs setup
                        </Tag>
                    )}
                </Space>
                <Button onClick={() => setReleaseDefaultsModalVisible(true)} disabled={isAmazonAppStore}>
                    Edit release defaults
                </Button>
            </div>
            <Text type="secondary">
                Configure default {displayStoreNameForAppStoreType(appStoreType)} settings and Runway will apply them
                where needed across releases.
            </Text>
            <div style={{ marginTop: 24, marginBottom: 12 }}>
                {isAppleStore && (
                    <AppleAppStoreReleaseDefaultsDescriptions
                        appId={props.appId}
                        orgId={props.orgId}
                        bordered={true}
                        labelStyle={{ width: 240 }}
                    />
                )}
                {isGoogleStore && (
                    <GooglePlayStoreReleaseSettingsDescriptions
                        {...props}
                        bordered={true}
                        isRollback={false}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        labelStyle={{ width: 240 }}
                        releaseSettings={appReleaseDefaults as GoogleAppStoreSettings}
                        isSubmitAutomationDisabledMissingDefaults={false}
                        stepRunComponentProps={null}
                    />
                )}
                {isHuaweiAppGallery && (
                    <HuaweiAppGalleryReleaseSettingsDescriptions
                        {...props}
                        bordered={true}
                        labelStyle={{ width: 240 }}
                        releaseSettings={appReleaseDefaults as HuaweiAppGallerySettings}
                        autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                        isSubmitAutomationDisabledMissingDefaults={false}
                    />
                )}
                {isAmazonAppStore && <AmazonAppStoreReleaseSettingsDescriptions />}
            </div>
            {isAppleStore && (
                <EditAppleAppReleaseDefaults
                    orgId={props.orgId}
                    appId={props.appId}
                    open={releaseDefaultsModalVisible}
                    onClose={() => setReleaseDefaultsModalVisible(false)}
                    onSuccess={() => setReleaseDefaultsModalVisible(false)}
                />
            )}
            {isGoogleStore && (
                <EditGoogleAppReleaseDefaults
                    orgId={props.orgId}
                    appId={props.appId}
                    open={releaseDefaultsModalVisible}
                    autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                    onClose={() => setReleaseDefaultsModalVisible(false)}
                    onSuccess={() => setReleaseDefaultsModalVisible(false)}
                />
            )}
            {isHuaweiAppGallery && (
                <EditHuaweiAppReleaseDefaults
                    orgId={props.orgId}
                    appId={props.appId}
                    open={releaseDefaultsModalVisible}
                    autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                    onClose={() => setReleaseDefaultsModalVisible(false)}
                    onSuccess={() => setReleaseDefaultsModalVisible(false)}
                />
            )}
        </div>
    );
};

export default AppReleaseDefaults;
